import { trackEvent } from 'dibs-tracking';
import {
    trackMixpanelUserLogin,
    trackMixpanelUserRegistration,
} from 'dibs-tracking/exports/mixpanel';
import get from 'lodash.get';
import {
    getTransactionsStatus,
    getFirstUtmSessionDate,
    getCurrentAccountType,
    formatDate,
    getCustomerType,
    CUSTOMER_TYPE_CONSUMER,
} from '../../utils/userTracking';

const ACTION_ENTRY = 'entry';
const ACTION_COMPLETE = 'complete';
const LABEL_LOGIN_LINK = 'login link';
const LABEL_REGISTER_LINK = 'register link';
const LABEL_UPDATE_PASSWORD_FLOW = 'account information link';

const TRADE_STATUS_MAP = {
    VERIFIED_VETTED: 'verified - vetted',
    VERIFIED_PRE_APPROVED: 'verified - pre-approved',
    SUSPECTED: 'suspected',
    APPLIED_IN_REVIEW: 'applied - in review',
    APPLIED_NOT_ENOUGH_INFORMATION: 'applied - not enough information',
    APPLIED_REJECTED: 'applied - rejected',
};

function registrationTracking({ action, label, value, additional }) {
    trackEvent({
        action: `registration ${action}`,
        category: 'registration',
        label,
        value,
        additional,
    });
}

function loginTracking({ action, label, value, additional }) {
    trackEvent({
        action: `login ${action}`,
        category: 'login',
        label,
        value,
        additional,
    });
}

function updatePassword({ action, label, value }) {
    trackEvent({
        action: `update password ${action}`,
        category: 'update password',
        label,
        value,
    });
}

function getTransactions(data = {}) {
    return {
        submittedTransactions: {
            totalResults: data.submittedTransactions,
        },
        confirmedTransactions: {
            totalResults: data.confirmedTransactions,
        },
        submittedTransactionsRecent: {
            totalResults: data.submittedTransactionsRecent,
        },
        confirmedTransactionsRecent: {
            totalResults: data.confirmedTransactionsRecent,
        },
    };
}

function getBuyerActivityStatusFromTransactions(transactions = {}) {
    const {
        submittedTransactions,
        confirmedTransactions,
        submittedTransactionsRecent,
        confirmedTransactionsRecent,
    } = transactions;

    const hasSubmittedTransactions = submittedTransactions && submittedTransactions.totalResults;
    const hasConfirmedTransactions = confirmedTransactions && confirmedTransactions.totalResults;
    const hasTransactions = hasSubmittedTransactions || hasConfirmedTransactions;

    const hasRecentSubmittedTransactions =
        submittedTransactionsRecent && submittedTransactionsRecent.totalResults;
    const hasRecentConfirmedTransactions =
        confirmedTransactionsRecent && confirmedTransactionsRecent.totalResults;
    const hasRecentTransactions = hasRecentSubmittedTransactions || hasRecentConfirmedTransactions;

    if (hasTransactions && hasRecentTransactions) {
        return 'active';
    } else if (hasTransactions && !hasRecentTransactions) {
        return 'lapsed';
    } else {
        return undefined;
    }
}

function getAdditionalRegistrationData(data = {}) {
    const userEmail = get(data, 'user.profile.email');
    const transactions = getTransactions(data);
    const registrationDate = formatDate(get(data, 'user.createdDate'));

    return {
        loginStatus: 'Logged In',
        userID: data.userId,
        registrationStatus: 'Registered',
        userEmailAddress: userEmail,
        emailToken: data.emailToken,
        defaultCurrency: 'USD Default',
        buyerStatus: getTransactionsStatus({ transactions, resetStorage: true }).buyerStatus,
        tradeStatus: undefined,
        customerType: CUSTOMER_TYPE_CONSUMER,
        registrationDate,
    };
}

function getAdditionalLoginData(data = {}) {
    const userCurrency = get(data, 'user.profile.currency');
    const userEmail = get(data, 'user.profile.email');
    const tradeStatus = get(data, 'user.verifiedTradeStatus');
    const transactions = getTransactions(data);
    const customerType = getCustomerType(get(data, 'user') || {});

    return {
        loginStatus: 'Logged In',
        userID: data.userId,
        registrationStatus: 'Registered',
        userEmailAddress: userEmail,
        defaultCurrency: `${userCurrency} Selected`,
        buyerStatus: getTransactionsStatus({ transactions, resetStorage: true }).buyerStatus,
        buyerActivityStatus: getBuyerActivityStatusFromTransactions(transactions),
        tradeStatus: TRADE_STATUS_MAP[tradeStatus],
        firstPurchasePrice: 'na',
        firstPurchaseDate: 'na',
        contactDealerStatus: undefined,
        customerType,
        firstSessionDate: getFirstUtmSessionDate(),
    };
}

export const trackRegistrationEntry = ({
    action = ACTION_ENTRY,
    label = LABEL_REGISTER_LINK,
    value,
}) => {
    registrationTracking({ action, label, value });
};

export const trackRegistrationComplete = trackData => {
    const { action = ACTION_COMPLETE, label = LABEL_REGISTER_LINK, value } = trackData;
    const additionalData = getAdditionalRegistrationData(trackData);
    registrationTracking({
        action,
        label,
        value,
        additional: additionalData,
    });
    trackMixpanelUserRegistration({
        triggerType: label,
        userId: additionalData.userID,
        userEmailAddress: additionalData.userEmailAddress,
        buyerStatus: additionalData.buyerStatus,
        customerType: additionalData.customerType,
    });
};

export const trackLoginEntry = ({ action = ACTION_ENTRY, label = LABEL_LOGIN_LINK, value }) => {
    loginTracking({ action, label, value });
};

export const trackContactLoginComplete = trackData => {
    const { action = ACTION_COMPLETE, label = LABEL_LOGIN_LINK, value } = trackData;
    const additionalData = getAdditionalLoginData(trackData);
    loginTracking({
        action,
        label,
        value,
        additional: additionalData,
    });
    trackMixpanelUserLogin({
        triggerType: label,
        userId: additionalData.userID,
        userEmailAddress: additionalData.userEmailAddress,
        buyerStatus: additionalData.buyerStatus,
        customerType: additionalData.customerType,
    });
};

export const trackUpdatePasswordEntry = () =>
    updatePassword({ action: ACTION_ENTRY, label: LABEL_UPDATE_PASSWORD_FLOW });
export const trackUpdatePasswordComplete = () =>
    updatePassword({ action: ACTION_COMPLETE, label: LABEL_UPDATE_PASSWORD_FLOW });

export function trackInputComplete({ field, triggerType }) {
    trackEvent({
        action: 'registration form interaction',
        category: 'registration',
        label: `registration step complete (${field})`,
        additional: {
            ...(triggerType ? { triggerType } : {}),
        },
    });
}

export function trackLoginError() {
    trackEvent({
        action: 'login|login modal|password',
        category: 'form error',
        label: 'incorrect password',
    });
}

export function trackRegistrationError({ field, error, action = 'register form' }) {
    trackEvent({
        action: `registration|${action}|${field}`,
        category: 'form error',
        label: error,
    });
}

export const trackSocialLoginSuccess = ({ label, value, ...trackData }) => {
    trackEvent({
        action: `login ${ACTION_COMPLETE}`,
        category: 'login',
        label,
        value,
        additional: getAdditionalLoginData(trackData),
    });
};

export const trackSocialLoginButtonClick = ({ category, label, value }) => {
    trackEvent({
        action: `${category} form interaction`,
        category,
        label,
        value,
        additional: { ssoType: 'facebook' },
    });

    trackEvent({
        action: 'facebook',
        category: 'social integrations',
        label: `connect account entry|${category} modal`,
        value,
    });
};

export const trackSocialLoginAccountLinked = ({ label, value, ...trackData }) => {
    trackEvent({
        action: `login ${ACTION_COMPLETE}`,
        category: 'login',
        label,
        value,
        additional: { ...getAdditionalLoginData(trackData), ssoType: 'facebook' },
    });

    trackEvent({
        action: 'facebook',
        category: 'social integrations',
        label: 'connect account successful|login modal',
        value: 0,
    });
};

export const trackSocialRegistrationComplete = ({ label, value, ...trackData }) => {
    const triggerType = trackData.triggerType ? { triggerType: trackData.triggerType } : {};
    trackEvent({
        action: 'registration form interaction',
        category: 'registration',
        label: 'registration step complete (email)',
        value,
        additional: {
            ...triggerType,
        },
    });

    trackEvent({
        action: 'registration form interaction',
        category: 'registration',
        label: 'registration step complete (password)',
        value,
        additional: {
            ...triggerType,
        },
    });

    trackEvent({
        action: `registration ${ACTION_COMPLETE}`,
        category: 'registration',
        label,
        value,
        additional: { ...getAdditionalRegistrationData(trackData), ssoType: 'facebook' },
    });

    trackEvent({
        action: 'facebook',
        category: 'social integrations',
        label: 'connect account successful|registration modal',
        value,
    });
};

export const trackSocialEmailOptOut = ({ label, value, errorMessage }) => {
    trackEvent({
        action: 'registration|register form|email',
        category: 'form error',
        label: errorMessage,
        value,
    });

    trackEvent({
        action: 'login entry',
        category: 'login',
        label,
        value,
        additional: { ssoType: 'facebook' },
    });
};

export const trackOnModalClose = (authResponse, ga) => {
    const { passwordAdded, accountCreated, loggedIn, socialLogin } = authResponse;
    const { label } = ga || {};
    trackEvent({
        category: 'registration',
        action: 'registration exit',
        label: label || 'none',
        additional: {
            currentAccountType: getCurrentAccountType({
                passwordAdded,
                accountCreated,
                loggedIn,
                socialLogin,
            }),
        },
    });
};
